<template>
  <aside
    class="fixed left-0 top-0 h-screen bg-dark-800 border-r border-dark-700 z-40 transition-all duration-300"
    :class="collapsed ? 'w-[72px]' : 'w-[260px]'"
  >
    <!-- Logo -->
    <div class="h-16 flex items-center gap-3 px-4 border-b border-dark-700">
      <div class="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
        <GlobeAltIcon class="w-6 h-6 text-white" />
      </div>
      <Transition name="fade">
        <div v-if="!collapsed" class="flex flex-col">
          <span class="font-bold text-lg gradient-text">XDNS</span>
          <span class="text-xs text-dark-500">Panel v2.0</span>
        </div>
      </Transition>
    </div>

    <!-- Navigation -->
    <nav class="p-3 space-y-1">
      <RouterLink
        v-for="item in menuItems"
        :key="item.path"
        :to="item.path"
        class="sidebar-item group"
        :class="{ 'sidebar-item-active': isActive(item.path) }"
      >
        <component :is="item.icon" class="w-5 h-5 flex-shrink-0" />
        <Transition name="fade">
          <span v-if="!collapsed" class="truncate">{{ item.label }}</span>
        </Transition>
        
        <!-- Tooltip on collapsed -->
        <div
          v-if="collapsed"
          class="absolute left-full ml-2 px-3 py-1.5 bg-dark-700 text-dark-100 text-sm rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50"
        >
          {{ item.label }}
        </div>
      </RouterLink>
    </nav>

    <!-- Separator -->
    <div class="mx-4 my-4 border-t border-dark-700" />

    <!-- Secondary Menu -->
    <nav class="px-3 space-y-1">
      <RouterLink
        to="/docs"
        class="sidebar-item group"
        :class="{ 'sidebar-item-active': isActive('/docs') }"
      >
        <BookOpenIcon class="w-5 h-5 flex-shrink-0" />
        <Transition name="fade">
          <span v-if="!collapsed">Documentação API</span>
        </Transition>
        <div
          v-if="collapsed"
          class="absolute left-full ml-2 px-3 py-1.5 bg-dark-700 text-dark-100 text-sm rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50"
        >
          Documentação API
        </div>
      </RouterLink>
    </nav>

    <!-- Collapse Button -->
    <button
      class="absolute bottom-4 right-4 p-2 rounded-lg bg-dark-700 hover:bg-dark-600 text-dark-400 hover:text-dark-100 transition-colors"
      @click="$emit('toggle')"
    >
      <ChevronLeftIcon
        class="w-5 h-5 transition-transform duration-300"
        :class="{ 'rotate-180': collapsed }"
      />
    </button>

    <!-- Server Status Mini -->
    <Transition name="slide-up">
      <div
        v-if="!collapsed"
        class="absolute bottom-16 left-3 right-3 p-3 bg-dark-900 rounded-lg border border-dark-700"
      >
        <div class="flex items-center justify-between mb-2">
          <span class="text-xs text-dark-500 font-medium uppercase tracking-wider">Servidores</span>
          <span 
            class="status-dot"
            :class="allServersOnline ? 'status-online' : 'status-warning'"
          />
        </div>
        <div class="space-y-1">
          <div 
            v-for="server in servers.slice(0, 3)" 
            :key="server.id"
            class="flex items-center justify-between text-sm"
          >
            <span class="text-dark-300 truncate">{{ server.name }}</span>
            <span 
              class="text-xs"
              :class="{
                'text-success-500': server.status === 'online',
                'text-warning-500': server.status === 'degraded',
                'text-danger-500': server.status === 'offline',
                'text-dark-500': server.status === 'unknown',
              }"
            >
              {{ server.status }}
            </span>
          </div>
        </div>
      </div>
    </Transition>
  </aside>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useDashboardStore } from '@/stores/dashboard'
import {
  GlobeAltIcon,
  ChartBarIcon,
  ServerStackIcon,
  DocumentTextIcon,
  ChartPieIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  BookOpenIcon,
  ChevronLeftIcon,
  ArrowPathRoundedSquareIcon,
} from '@heroicons/vue/24/outline'

defineProps<{
  collapsed: boolean
}>()

defineEmits<{
  toggle: []
}>()

const route = useRoute()
const dashboardStore = useDashboardStore()

const servers = computed(() => dashboardStore.servers)
const allServersOnline = computed(() => dashboardStore.allServersOnline)

const menuItems = [
  { path: '/', label: 'Dashboard', icon: ChartBarIcon },
  { path: '/zones', label: 'Zonas DNS', icon: GlobeAltIcon },
  { path: '/records', label: 'Registros', icon: DocumentTextIcon },
  { path: '/servers', label: 'Servidores', icon: ServerStackIcon },
  { path: '/recursor', label: 'Recursor', icon: ArrowPathRoundedSquareIcon },
  { path: '/analytics', label: 'Analytics', icon: ChartPieIcon },
  { path: '/audit', label: 'Auditoria', icon: ClipboardDocumentListIcon },
  { path: '/settings', label: 'Configurações', icon: Cog6ToothIcon },
]

function isActive(path: string): boolean {
  if (path === '/') {
    return route.path === '/'
  }
  return route.path.startsWith(path)
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.2s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
